import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import {
  createSharingLink,
  deleteSharingLink,
  getAgentAnalytics,
  getSharingLinkById,
  getShareLinkStats,
  listSharingLinks,
  updateSharingLink
} from '../services/sharingLinksService'

const DEFAULT_STALE_TIME = 1000 * 60 * 2
const DEFAULT_CACHE_TIME = 1000 * 60 * 10

export const legacyShareLinksKeys = {
  all: ['shareLinks'],
  lists: () => [...legacyShareLinksKeys.all, 'list'],
  list: (options = {}) => [...legacyShareLinksKeys.lists(), options],
  details: () => [...legacyShareLinksKeys.all, 'detail'],
  detail: (linkId, options = {}) => [...legacyShareLinksKeys.details(), linkId, options]
}

export const shareLinksKeys = {
  all: ['agentShareLinks'],
  lists: () => [...shareLinksKeys.all, 'list'],
  list: (options = {}) => [...shareLinksKeys.lists(), options],
  details: () => [...shareLinksKeys.all, 'detail'],
  detail: (linkId, options = {}) => [...shareLinksKeys.details(), linkId, options],
  stats: () => [...shareLinksKeys.all, 'stats'],
  statsDetail: (linkId, options = {}) => [...shareLinksKeys.stats(), linkId, options],
  analytics: () => [...shareLinksKeys.all, 'analytics'],
  agentAnalytics: (agentId, options = {}) => [
    ...shareLinksKeys.analytics(),
    agentId,
    options
  ]
}

const subscribeToShareLinks = (queryClient) => {
  const channel = supabase
    .channel('share-links-realtime')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'agent_sharing_links' },
      () => {
        queryClient.invalidateQueries({ queryKey: shareLinksKeys.all })
        queryClient.invalidateQueries({ queryKey: legacyShareLinksKeys.all })
      }
    )
    .subscribe()

  return () => {
    supabase.removeChannel(channel)
  }
}

export const useShareLinks = (options = {}, config = {}) => {
  const {
    enabled = true,
    realtime = false,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!realtime) return undefined
    return subscribeToShareLinks(queryClient)
  }, [queryClient, realtime])

  return useQuery({
    queryKey: shareLinksKeys.list(options),
    queryFn: async () => {
      const result = await listSharingLinks(options)
      if (result.error) {
        throw result.error
      }
      return result.data
    },
    enabled,
    staleTime,
    cacheTime
  })
}

export const useShareLink = (linkId, options = {}, config = {}) => {
  const {
    enabled = true,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config

  return useQuery({
    queryKey: shareLinksKeys.detail(linkId, options),
    queryFn: async () => {
      const result = await getSharingLinkById(linkId, options)
      if (result.error) {
        throw result.error
      }
      return result.data
    },
    enabled: enabled && Boolean(linkId),
    staleTime,
    cacheTime
  })
}

export const useCreateShareLink = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload) => {
      const result = await createSharingLink(payload, options)
      if (result.error) {
        throw result.error
      }
      return result.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(shareLinksKeys.detail(data.id, options), data)
      queryClient.invalidateQueries({ queryKey: shareLinksKeys.lists() })
      queryClient.invalidateQueries({ queryKey: legacyShareLinksKeys.lists() })
    }
  })
}

export const useUpdateShareLink = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ linkId, updates }) => updateSharingLink(linkId, updates, options),
    onMutate: async ({ linkId, updates }) => {
      await queryClient.cancelQueries({ queryKey: shareLinksKeys.all })

      const previousLists = queryClient.getQueriesData({
        queryKey: shareLinksKeys.lists()
      })
      const previousDetails = queryClient.getQueriesData({
        queryKey: shareLinksKeys.details()
      })

      previousLists.forEach(([queryKey, data]) => {
        if (!Array.isArray(data)) return
        queryClient.setQueryData(
          queryKey,
          data.map((item) =>
            item.id === linkId ? { ...item, ...updates } : item
          )
        )
      })

      previousDetails.forEach(([queryKey, data]) => {
        if (!data || data.id !== linkId) return
        queryClient.setQueryData(queryKey, { ...data, ...updates })
      })

      return { previousLists, previousDetails }
    },
    onError: (_error, _variables, context) => {
      if (!context) return
      context.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context.previousDetails?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: shareLinksKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: shareLinksKeys.detail(variables.linkId)
      })
      queryClient.invalidateQueries({ queryKey: legacyShareLinksKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: legacyShareLinksKeys.detail(variables.linkId)
      })
    }
  })
}

export const useDeleteShareLink = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (linkId) => deleteSharingLink(linkId, options),
    onMutate: async (linkId) => {
      await queryClient.cancelQueries({ queryKey: shareLinksKeys.all })

      const previousLists = queryClient.getQueriesData({
        queryKey: shareLinksKeys.lists()
      })
      const previousDetails = queryClient.getQueriesData({
        queryKey: shareLinksKeys.details()
      })

      previousLists.forEach(([queryKey, data]) => {
        if (!Array.isArray(data)) return
        queryClient.setQueryData(
          queryKey,
          data.filter((item) => item.id !== linkId)
        )
      })

      previousDetails.forEach(([queryKey, data]) => {
        if (!data || data.id !== linkId) return
        queryClient.removeQueries({ queryKey, exact: true })
      })

      return { previousLists, previousDetails }
    },
    onError: (_error, _linkId, context) => {
      if (!context) return
      context.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context.previousDetails?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: (_data, _error, linkId) => {
      queryClient.invalidateQueries({ queryKey: shareLinksKeys.lists() })
      queryClient.invalidateQueries({ queryKey: shareLinksKeys.detail(linkId) })
      queryClient.invalidateQueries({ queryKey: legacyShareLinksKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: legacyShareLinksKeys.detail(linkId)
      })
    }
  })
}

export const useShareLinkStats = (linkId, config = {}) => {
  const {
    enabled = true,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config

  return useQuery({
    queryKey: shareLinksKeys.statsDetail(linkId),
    queryFn: async () => {
      const result = await getShareLinkStats(linkId)
      if (result.error) {
        throw result.error
      }
      return result.data
    },
    enabled: enabled && Boolean(linkId),
    staleTime,
    cacheTime
  })
}

export const useAgentSharingAnalytics = (agentId, config = {}) => {
  const {
    enabled = true,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config

  return useQuery({
    queryKey: shareLinksKeys.agentAnalytics(agentId),
    queryFn: async () => {
      const result = await getAgentAnalytics(agentId)
      if (result.error) {
        throw result.error
      }
      return result.data
    },
    enabled: enabled && Boolean(agentId),
    staleTime,
    cacheTime
  })
}
